import { useCallback, useContext } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { AuthContext } from 'auth';
import {
  CREATE_COMMENT,
  DELETE_COMMENT,
  GET_USER_BY_UUID,
  GET_COMMENTS,
  ICreateCommentsRequest,
  ICreateCommentsResponse,
  IDeleteCommentsRequest,
  IDeleteCommentsResponse,
  IGetCommentsRequest,
  IGetCommentsResponse,
  IUpdateCommentsRequest,
  IUpdateCommentsResponse,
  UPDATE_COMMENT,
} from 'schemas';
import { IComment, TCommentEntityType } from 'types';

interface IReturnValue {
  comments: Array<IComment>;
  commentsLoading: boolean;
  createComment: (text: string, is_important: boolean) => Promise<unknown>;
  deleteComment: (id: number) => Promise<unknown>;
  updateComment: (id: number, is_important: boolean) => Promise<unknown>;
  updateLoading: boolean;
}

export function useComments(
  entityId: number,
  type: TCommentEntityType
): IReturnValue {
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data, loading } = useQuery<IGetCommentsResponse, IGetCommentsRequest>(
    GET_COMMENTS,
    {
      variables: { input: { entity_id: entityId, type } },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !permissions?.has('comment_read'),
    }
  );

  const [createCommentReq] = useMutation<
    ICreateCommentsResponse,
    ICreateCommentsRequest
  >(CREATE_COMMENT, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GET_USER_BY_UUID, GET_COMMENTS],
    awaitRefetchQueries: true,
  });

  const [updateCommentReq, { loading: updateLoading }] = useMutation<
    IUpdateCommentsResponse,
    IUpdateCommentsRequest
  >(UPDATE_COMMENT, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GET_USER_BY_UUID, GET_COMMENTS],
    awaitRefetchQueries: true,
  });

  const [deleteCommentReq] = useMutation<
    IDeleteCommentsResponse,
    IDeleteCommentsRequest
  >(DELETE_COMMENT, {
    fetchPolicy: 'no-cache',
    refetchQueries: [GET_USER_BY_UUID, GET_COMMENTS],
    awaitRefetchQueries: true,
  });

  const createComment = useCallback(
    (text: string, is_important: boolean) =>
      createCommentReq({
        variables: {
          input: {
            text,
            entity_id: entityId,
            type,
            is_important,
          },
        },
      }).catch(console.error),
    [createCommentReq, entityId]
  );

  const updateComment = useCallback(
    (id: number, is_important: boolean) =>
      updateCommentReq({
        variables: {
          id,
          input: {
            is_important,
          },
        },
      }).catch(console.error),
    [updateCommentReq, entityId]
  );

  const deleteComment = useCallback(
    (id) => deleteCommentReq({ variables: { id } }).catch(console.error),
    [deleteCommentReq]
  );
  return {
    comments: data?.comments?.data ?? [],
    commentsLoading: loading && !data,
    createComment,
    deleteComment,
    updateComment,
    updateLoading,
  };
}
